/**
 * TeamLeave — who is off, and who signed it off.
 * @file src/web/features/leave/components/TeamLeave.tsx
 *
 * @llm-rule WHEN: A manager, CTO or CEO opens Leave
 * @llm-rule NOTE: Reach follows the same reporting line as everything else, so
 *   a lead sees their employees, a Head sees leads and employees, and the Company Admin
 *   sees the Heads too. The DECIDER is always named: a Company Admin must be able to see
 *   that a Head approved something without asking anyone.
 * @llm-rule AVOID: Nesting this inside the page component — it would remount on
 *   every keystroke of the apply form and lose scroll position.
 */

import { CalendarCheck2, CircleUser, Plane } from 'lucide-react';
import { PersonAvatar, SectionLabel } from '../../../shared/components/presence';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import {
  LEAVE_STATUS_TONE,
  LEAVE_TYPE_LABEL,
  describeSpan,
  type LeaveRequest,
} from '../leave.types';

/** Local YYYY-MM-DD, so "today" means the viewer's today. */
function todayKey(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function shortDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Who decided, in a form a person can act on. Rank matters here — "approved by
 * Meera Iyer · CTO" answers "was this signed off by someone senior enough?"
 * without a second lookup.
 */
function Decider({ row }: { row: LeaveRequest }) {
  if (row.status === 'PENDING') {
    return <span className="text-[11.5px] text-ink-faint">Not decided yet</span>;
  }
  if (!row.decidedBy) {
    return <span className="text-[11.5px] text-ink-faint">{row.status.toLowerCase()}</span>;
  }

  const verb = row.status === 'APPROVED' ? 'Approved' : row.status === 'REJECTED' ? 'Rejected' : 'Closed';

  return (
    <span className="flex flex-wrap items-center gap-1 text-[11.5px] text-ink-faint">
      <CircleUser className="size-3.5 flex-none" strokeWidth={1.8} />
      {verb} by
      <b className="font-semibold text-ink-soft">{row.decidedBy.name}</b>
      <span className="text-ink-ghost">· {ROLE_LABEL[row.decidedBy.orgRole]}</span>
      {row.decidedAt && <span className="text-ink-ghost">· {shortDate(row.decidedAt)}</span>}
    </span>
  );
}

function LeaveRow({ row }: { row: LeaveRequest }) {
  const tone = LEAVE_STATUS_TONE[row.status];

  return (
    <li className="flex flex-wrap items-start gap-3 px-5 py-3.5">
      {row.user && <PersonAvatar person={row.user} size={30} />}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[13px] font-medium text-ink">{row.user?.name ?? 'Someone'}</span>
          <span className="text-[11.5px] text-ink-ghost">
            {row.user?.title || (row.user && ROLE_LABEL[row.user.orgRole as OrgRole])}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]"
            style={{ background: tone.bg, color: tone.fg }}
          >
            {row.status.toLowerCase()}
          </span>
        </div>

        <div className="mt-0.5 text-[12.5px] text-ink-soft">
          {LEAVE_TYPE_LABEL[row.type]} · {describeSpan(row)}
        </div>

        {row.reason && (
          <p className="mt-0.5 text-[12px] leading-relaxed text-ink-faint">“{row.reason}”</p>
        )}

        <div className="mt-1">
          <Decider row={row} />
        </div>

        {row.decisionNote && (
          <p className="mt-0.5 text-[11.5px] italic text-ink-faint">“{row.decisionNote}”</p>
        )}
      </div>
    </li>
  );
}

function Group({
  icon: Icon,
  title,
  rows,
  empty,
}: {
  icon: typeof Plane;
  title: string;
  rows: LeaveRequest[];
  empty: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 border-b border-rule px-5 py-3">
        <Icon className="size-4 text-ink-faint" strokeWidth={1.8} />
        <SectionLabel>{title}</SectionLabel>
        <span className="tnum ml-auto rounded-full bg-paper-sunk px-2 py-0.5 text-[11px] font-semibold text-ink-faint">
          {rows.length}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-6 text-center text-[12.5px] text-ink-faint">{empty}</p>
      ) : (
        <ul className="divide-y divide-rule">
          {rows.map((row) => (
            <LeaveRow key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function TeamLeave({ rows }: { rows: LeaveRequest[] }) {
  const today = todayKey();

  const approved = rows.filter((r) => r.status === 'APPROVED');

  // Off right now — the answer to "who is not at their desk today?"
  const offToday = approved.filter(
    (r) => r.startDate.slice(0, 10) <= today && r.endDate.slice(0, 10) >= today,
  );
  const upcoming = approved.filter((r) => r.startDate.slice(0, 10) > today);

  // Everything already decided, most recent first — this is where a Company Admin sees
  // that a Head approved something.
  const decided = rows
    .filter((r) => r.status !== 'PENDING' && !offToday.includes(r) && !upcoming.includes(r))
    .slice(0, 25);

  return (
    <div className="mb-5 flex flex-col gap-4">
      <div className="overflow-hidden rounded-[18px] border border-rule bg-paper elev-1">
        <Group
          icon={Plane}
          title="Off today"
          rows={offToday}
          empty="Everyone in your line is in today."
        />
      </div>

      <div className="overflow-hidden rounded-[18px] border border-rule bg-paper elev-1">
        <Group
          icon={CalendarCheck2}
          title="Approved, coming up"
          rows={upcoming}
          empty="No approved leave ahead."
        />
      </div>

      <div className="overflow-hidden rounded-[18px] border border-rule bg-paper elev-1">
        <Group
          icon={CircleUser}
          title="Already decided"
          rows={decided}
          empty="Nothing decided yet."
        />
      </div>
    </div>
  );
}
